import React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowUp, Square, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { DEBUG_CHAT, debugLog } from "@/config/debug";
import { useRenderCount } from "@/hooks/useRenderCount";

// Textarea Component
interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  className?: string;
}
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(({ className, ...props }, ref) => (
  <textarea
    className={cn(
      "flex w-full rounded-md border-none bg-transparent px-3 py-2.5 text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 min-h-[44px] resize-none scrollbar-thin",
      className
    )}
    ref={ref}
    rows={1}
    {...props}
  />
));
Textarea.displayName = "Textarea";

// Tooltip Components
const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// Dialog Components
const Dialog = DialogPrimitive.Root;
const DialogPortal = DialogPrimitive.Portal;
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-[90vw] md:max-w-[800px] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-0 shadow-xl duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-2xl",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 z-10 rounded-full bg-muted/80 p-2 hover:bg-muted transition-all">
        <X className="h-5 w-5 text-muted-foreground hover:text-foreground" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight text-foreground", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

// Button Component
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", ...props }, ref) => {
    const variantClasses = {
      default: "bg-primary hover:bg-primary/90 text-primary-foreground",
      outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
      ghost: "bg-transparent hover:bg-accent hover:text-accent-foreground",
    };
    const sizeClasses = {
      default: "h-10 px-4 py-2",
      sm: "h-8 px-3 text-sm",
      lg: "h-12 px-6",
      icon: "h-8 w-8 rounded-full aspect-[1/1]",
    };
    return (
      <button
        className={cn(
          "inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 rounded-md",
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

// VoiceRecorder Component
interface VoiceRecorderProps {
  isRecording: boolean;
  onStartRecording: () => void;
  onStopRecording: (duration: number) => void;
  duration?: number;
  visualizerBars?: number;
}
const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  isRecording,
  onStartRecording,
  onStopRecording,
  duration = 0,
  visualizerBars = 32,
}) => {
  const [time, setTime] = React.useState(duration);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    setTime(duration);
  }, [duration]);

  React.useEffect(() => {
    if (isRecording && !timerRef.current) {
      onStartRecording();
      timerRef.current = setInterval(() => setTime((t) => t + 1), 1000);
    } else if (!isRecording && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      onStopRecording(time);
      setTime(0);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording, onStartRecording, onStopRecording, time]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center w-full transition-all duration-300 py-3",
        isRecording ? "opacity-100" : "opacity-0 h-0"
      )}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="h-2 w-2 rounded-full bg-destructive animate-pulse" />
        <span className="font-mono text-sm text-muted-foreground">{formatTime(time)}</span>
      </div>
      <div className="w-full h-10 flex items-center justify-center gap-0.5 px-4">
        {[...Array(visualizerBars)].map((_, i) => (
          <div
            key={i}
            className="w-0.5 rounded-full bg-primary/50 animate-pulse"
            style={{
              height: `${Math.max(15, Math.random() * 100)}%`,
              animationDelay: `${i * 0.05}s`,
              animationDuration: `${0.5 + Math.random() * 0.5}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

// ImageViewDialog Component
interface ImageViewDialogProps {
  imageUrl: string | null;
  onClose: () => void;
}
const ImageViewDialog: React.FC<ImageViewDialogProps> = ({ imageUrl, onClose }) => {
  if (!imageUrl) return null;
  return (
    <Dialog open={!!imageUrl} onOpenChange={onClose}>
      <DialogContent className="p-0 border-none bg-transparent shadow-none max-w-[90vw] md:max-w-[800px]">
        <DialogTitle className="sr-only">Image Preview</DialogTitle>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="relative bg-background rounded-2xl overflow-hidden shadow-2xl"
        >
          <img
            src={imageUrl}
            alt="Full preview"
            className="w-full max-h-[80vh] object-contain rounded-2xl"
          />
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

// PromptInput Context and Components
interface PromptInputContextType {
  isLoading: boolean;
  value: string;
  setValue: (value: string) => void;
  maxHeight: number | string;
  onSubmit?: () => void;
  disabled?: boolean;
}
const PromptInputContext = React.createContext<PromptInputContextType>({
  isLoading: false,
  value: "",
  setValue: () => {},
  maxHeight: 240,
  onSubmit: undefined,
  disabled: false,
});
function usePromptInput() {
  const context = React.useContext(PromptInputContext);
  if (!context) throw new Error("usePromptInput must be used within a PromptInput");
  return context;
}

interface PromptInputProps {
  isLoading?: boolean;
  value?: string;
  onValueChange?: (value: string) => void;
  maxHeight?: number | string;
  onSubmit?: () => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
}
const PromptInput = React.forwardRef<HTMLDivElement, PromptInputProps>(
  (
    {
      className,
      isLoading = false,
      maxHeight = 240,
      value,
      onValueChange,
      onSubmit,
      children,
      disabled = false,
      onDragOver,
      onDragLeave,
      onDrop,
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(value || "");
    const handleChange = (newValue: string) => {
      setInternalValue(newValue);
      onValueChange?.(newValue);
    };
    return (
      <TooltipProvider>
        <PromptInputContext.Provider
          value={{
            isLoading,
            value: value ?? internalValue,
            setValue: onValueChange ?? handleChange,
            maxHeight,
            onSubmit,
            disabled,
          }}
        >
          <div
            ref={ref}
            className={cn(
              "rounded-3xl border bg-background p-2 transition-all duration-300",
              isLoading && "border-destructive/70",
              className
            )}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            {children}
          </div>
        </PromptInputContext.Provider>
      </TooltipProvider>
    );
  }
);
PromptInput.displayName = "PromptInput";

interface PromptInputTextareaProps {
  disableAutosize?: boolean;
  placeholder?: string;
  onFocusChange?: (focused: boolean) => void;
}
  const PromptInputTextarea = React.memo(React.forwardRef<HTMLTextAreaElement, PromptInputTextareaProps & React.ComponentProps<typeof Textarea>>(({
    className,
    onKeyDown,
    disableAutosize = false,
    placeholder,
    onFocus,
    onBlur,
    onFocusChange,
    ...props
  }, externalRef) => {
    const { value, setValue, maxHeight, onSubmit, disabled } = usePromptInput();
    const internalRef = React.useRef<HTMLTextAreaElement>(null);
    
    // Merge refs: use external ref if provided, otherwise internal
    const mergedRef = React.useCallback((element: HTMLTextAreaElement) => {
      // Set the internal ref for auto-resize logic
      internalRef.current = element;
      
      // Call the external ref if provided
      if (typeof externalRef === 'function') {
        externalRef(element);
      } else if (externalRef) {
        externalRef.current = element;
      }
    }, [externalRef]);

    // Auto-resize effect - depende do value para funcionar corretamente
    React.useEffect(() => {
      if (disableAutosize || !internalRef.current) return;
      
      // Reset height to auto to get correct scrollHeight
      internalRef.current.style.height = "auto";
      
      // Calculate new height with constraints
      const scrollHeight = internalRef.current.scrollHeight;
      const newHeight = typeof maxHeight === "number"
        ? Math.min(scrollHeight, maxHeight)
        : scrollHeight;
      
      internalRef.current.style.height = `${newHeight}px`;
      
      // Enable overflow if content exceeds max height
      if (typeof maxHeight === "number" && scrollHeight > maxHeight) {
        internalRef.current.style.overflowY = "auto";
      } else {
        internalRef.current.style.overflowY = "hidden";
      }
    }, [value, maxHeight, disableAutosize]); // CRÍTICO: incluir 'value' nas dependências

    // **ETAPA 2: FOCO SIMPLES E DIRETO**
    const handleFocus = React.useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
      onFocusChange?.(true);
      onFocus?.(e);
    }, [onFocus, onFocusChange]);

    const handleBlur = React.useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
      onFocusChange?.(false);
      onBlur?.(e);
    }, [onBlur, onFocusChange]);

    // **ETAPA 2: POINTER DOWN PARA CONTROLE DE FOCO**
    const handlePointerDown = React.useCallback((e: React.PointerEvent<HTMLTextAreaElement>) => {
      e.stopPropagation();
      const target = e.currentTarget as HTMLTextAreaElement;
      requestAnimationFrame(() => {
        target.focus();
        const len = target.value.length;
        try { 
          target.setSelectionRange(len, len); 
        } catch {}
      });
    }, []);

    // **ETAPA 2: DETECTOR DE REMOUNT E CLOBBER** 
    React.useEffect(() => {
      if (!DEBUG_CHAT) return;

      const taId = "chat-textarea-debug";
      const ta = document.getElementById(taId) as HTMLTextAreaElement | null;
      if (!ta) { 
        debugLog.warn('[DBG TA] textarea não encontrado'); 
        return; 
      }

      debugLog.textarea(`[DBG TA] init - present: ${!!ta}`);

      // 1) Observa mudanças de valor (clobber externo)
      let lastValue = ta.value;
      const valueInterval = setInterval(() => {
        if (ta.value !== lastValue) {
          debugLog.textareaValue({ from: lastValue, to: ta.value });
          lastValue = ta.value;
        }
      }, 120);

      // 2) Observa remoções/adições do textarea no DOM (remount)
      const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          mutation.removedNodes.forEach(node => { 
            if ((node as any).id === taId) {
              debugLog.error('[DBG TA] REMOVIDO do DOM (remount)'); 
            }
          });
          mutation.addedNodes.forEach(node => { 
            if ((node as any).id === taId) {
              debugLog.error('[DBG TA] ADICIONADO ao DOM (remount)'); 
            }
          });
        });
      });
      mutationObserver.observe(document.body, { childList: true, subtree: true });

      // 3) Observa foco
      const onFocus = () => debugLog.textarea('[DBG TA] focus');
      const onBlur = () => debugLog.textarea('[DBG TA] blur');
      ta.addEventListener('focus', onFocus, true);
      ta.addEventListener('blur', onBlur, true);

      return () => {
        clearInterval(valueInterval);
        mutationObserver.disconnect();
        ta.removeEventListener('focus', onFocus, true);
        ta.removeEventListener('blur', onBlur, true);
      };
    }, []);

    return (
      <Textarea
        id="chat-textarea-debug"
        data-prompt-input="true"
        ref={mergedRef}
        value={value}
        onBeforeInputCapture={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            // PRIORIDADE 1: Check for IME composition
            const composing = (e as any).isComposing || (e.nativeEvent as any)?.isComposing;
            if (composing) return;
            
            // PRIORIDADE 2: Don't submit with modifier keys (Shift+Enter = new line)
            if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
            
            // PRIORIDADE 3: Submit message
            e.preventDefault();
            e.stopPropagation(); // Previne propagação apenas quando enviar
            // CRITICAL: onSubmit is always provided via context (line 718)
            if (onSubmit) onSubmit();
          }
        }}
        onChange={(e) => {
          const newValue = e.target.value;
          setValue(newValue); // CRÍTICO: atualizar o valor no estado
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onPointerDown={handlePointerDown}
        className={cn("text-base flex-1 resize-none", className)}
        placeholder={placeholder}
        disabled={disabled}
        {...props}
      />
    );
  }));
  PromptInputTextarea.displayName = "PromptInputTextarea";

  interface PromptInputActionsProps extends React.HTMLAttributes<HTMLDivElement> {}

  const PromptInputActions: React.FC<PromptInputActionsProps> = ({ children, className, ...props }) => (
    <div className={cn("flex items-center gap-2", className)} {...props}>
      {children}
    </div>
  );

interface PromptInputActionProps extends React.ComponentProps<typeof Tooltip> {
  tooltip: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
}
const PromptInputAction: React.FC<PromptInputActionProps> = ({
  tooltip,
  children,
  className,
  side = "top",
  ...props
}) => {
  const { disabled } = usePromptInput();
  return (
    <Tooltip {...props}>
      <TooltipTrigger asChild disabled={disabled}>
        {children}
      </TooltipTrigger>
      <TooltipContent side={side} className={className}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

// Main PromptInputBox Component
interface PromptInputBoxProps {
  onSend?: (message: string, files?: File[]) => void;
  isLoading?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  // Drag and drop handlers
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  // Paste handler
  onPaste?: (e: React.ClipboardEvent) => void;
  // Focus change handler
  onFocusChange?: (focused: boolean) => void;
  // Option to disable auto-resize (to match general chat behavior)
  disableAutoResize?: boolean;
}

export const PromptInputBox = React.memo(React.forwardRef<HTMLDivElement, PromptInputBoxProps>((props, ref) => {
  // **ETAPA 3: CONTADOR DE RE-RENDERS**
  useRenderCount('PromptInputBox');
  
  const { 
    onSend = () => {}, 
    isLoading = false,
    placeholder = "Faça perguntas...", 
    className,
    disabled = false,
    onDragOver,
    onDragLeave, 
    onDrop,
    onPaste,
    onFocusChange,
    disableAutoResize = false
  } = props;

  const [input, setInput] = React.useState("");
  const [isFocused, setIsFocused] = React.useState(false);
  const promptBoxRef = React.useRef<HTMLDivElement>(null);

  // **ETAPA 2: FOCO DIRIGIDO PELO TEXTAREA**
  const handleFocusChange = React.useCallback((focused: boolean) => {
    setIsFocused(focused);
    onFocusChange?.(focused);
  }, [onFocusChange]);


  const handleSubmit = React.useCallback(() => {
    if (input.trim()) {
      onSend(input);
      setInput("");
    }
  }, [input, onSend]);

  const hasContent = React.useMemo(() => input.trim() !== "", [input]);

  return (
    <>
      <PromptInput
        value={input}
        onValueChange={setInput}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        className={cn(
          "w-full bg-background border-input transition-all duration-300 ease-in-out",
          className
        )}
        disabled={disabled || isLoading}
        ref={ref || promptBoxRef}
      >
        <PromptInputActions className="flex items-center gap-2 p-0 pt-2">
          <PromptInputTextarea
            placeholder={placeholder}
            className="text-base flex-1 min-h-[32px] !py-1 leading-normal"
            onPaste={onPaste}
            disableAutosize={disableAutoResize}
            onFocusChange={handleFocusChange}
          />

          <PromptInputAction tooltip={hasContent ? "Enviar mensagem" : "Digite uma mensagem"}>
            <Button
              variant="default"
              size="icon"
              className={cn(
                "h-8 w-8 rounded-full transition-all duration-200 flex-shrink-0",
                hasContent
                  ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                  : "bg-transparent hover:bg-accent text-muted-foreground hover:text-accent-foreground"
              )}
              onClick={handleSubmit}
              disabled={!hasContent || isLoading}
            >
              {isLoading ? (
                <Square className="h-4 w-4 fill-current animate-pulse" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
            </Button>
          </PromptInputAction>
        </PromptInputActions>
      </PromptInput>
    </>
  );
}));
PromptInputBox.displayName = "PromptInputBox";
